import { useEffect } from 'react';
import {
  flattenJSON,
  normalizeContextKeys,
} from '~/services/pipes/extra-context-keys.ts';
import { requestJsonData } from '~/utils/request-json-data.ts';
import { getClientFeedUrl } from '~/services/request';
import { AUTH_STATE_CHANGED_EVENT } from '~/utils/is-logged-in-client-state.ts';
import {
  CONTEXT_SETTER_REVALIDATE_STORAGE_PREFIX,
  dispatchContextSetterReadyEvent,
  hasStoredContextSetterKeys,
  markRemoteContextResolved,
  storeContextSetterKeys,
  type ClientContextKeys,
} from '~/utils/context-setter-storage.ts';

const CONTEXT_SETTER_REVALIDATE_MS = 600_000;

type PersistentKeysPayload = {
  persistent_keys?: {
    content?: Record<string, Record<string, unknown>>;
  };
};

/**
 * Fetches and revalidates a remote Context Setter feed whenever the provided URL changes.
 *
 * @param contextSetterRemoteUrl - Remote Context Setter feed URL.
 * @returns Nothing.
 */
export function useContextSetterRemoteFeed(
  contextSetterRemoteUrl: string
): void {
  useEffect(() => {
    if (!contextSetterRemoteUrl) return;

    void fetchRemoteContextFeedWithRevalidation(contextSetterRemoteUrl);
  }, [contextSetterRemoteUrl]);

  useEffect(() => {
    if (!contextSetterRemoteUrl) return;

    const handleAuthStateChanged = (): void => {
      void fetchRemoteContextFeedWithRevalidation(contextSetterRemoteUrl, {
        forceFresh: true,
      });
    };

    window.addEventListener(AUTH_STATE_CHANGED_EVENT, handleAuthStateChanged);

    return () => {
      window.removeEventListener(
        AUTH_STATE_CHANGED_EVENT,
        handleAuthStateChanged
      );
    };
  }, [contextSetterRemoteUrl]);
}

function asPersistentKeysPayload(
  value: unknown
): PersistentKeysPayload | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  return value as PersistentKeysPayload;
}

/**
 * Extracts namespaced content from a Context Setter remote feed payload.
 *
 * @param payload - Remote feed payload.
 * @returns Content object keyed by namespace, or `undefined` when payload shape is invalid or missing.
 */
export function extractPersistentKeysContent(
  payload: unknown
): Record<string, Record<string, unknown>> | undefined {
  const persistentKeysPayload: PersistentKeysPayload | undefined =
    asPersistentKeysPayload(payload);
  const nestedFeedPayload: PersistentKeysPayload | undefined =
    asPersistentKeysPayload(
      (payload as Record<string, unknown> | null | undefined)?.feed
    );

  const content: Record<string, Record<string, unknown>> | undefined =
    persistentKeysPayload?.persistent_keys?.content ??
    nestedFeedPayload?.persistent_keys?.content;

  if (!content || typeof content !== 'object') return undefined;

  return content;
}

/**
 * Flattens a Context Setter remote feed payload into flat context keys.
 *
 * @param payload - Remote feed payload.
 * @returns Flat validated context keys.
 */
export function flattenContextPayload(payload: unknown): ClientContextKeys {
  const persistentContent: Record<string, Record<string, unknown>> | undefined =
    extractPersistentKeysContent(payload);

  if (!persistentContent) return {};

  const flattenedContextKeys: ClientContextKeys = {};

  Object.entries(persistentContent).forEach(
    ([namespace, entries]: [string, Record<string, unknown>]) => {
      if (!entries || typeof entries !== 'object' || Array.isArray(entries)) {
        return;
      }

      const flattenedEntries: Record<string, unknown> = flattenJSON(entries);

      Object.entries(flattenedEntries).forEach(
        ([key, value]: [string, unknown]) => {
          flattenedContextKeys[`${namespace}.${key}`] = value as
            | string
            | number
            | boolean;
        }
      );
    }
  );

  return normalizeContextKeys(flattenedContextKeys);
}

/**
 * Stores Context Setter remote feed data under a single sessionStorage key.
 *
 * @param payload - Feed payload to flatten and persist.
 * @returns Nothing.
 */
export function storeContextPayloadInStorage(payload: unknown): void {
  const clientContextKeys: ClientContextKeys = flattenContextPayload(payload);

  storeContextSetterKeys(clientContextKeys);
}

/**
 * Fetches remote Context Setter data with sessionStorage-based time revalidation.
 *
 * Dispatches a ready event immediately if local context already exists, then fetches
 * fresh data when keys are missing or the revalidation window has expired.
 *
 * @param contextSetterRemoteUrl - Remote Context Setter feed URL.
 * @returns A promise that resolves when the revalidation flow is complete.
 */
export async function fetchRemoteContextFeedWithRevalidation(
  contextSetterRemoteUrl: string,
  options?: { forceFresh?: boolean }
): Promise<void> {
  const forceFresh: boolean = options?.forceFresh ?? false;

  const clientFeedUrl: string = forceFresh
    ? getClientFeedUrlWithForceFresh(contextSetterRemoteUrl)
    : `/${getClientFeedUrl(contextSetterRemoteUrl)}`;

  if (!forceFresh && hasStoredContextSetterKeys()) {
    announceRemoteContextReady(contextSetterRemoteUrl);

    if (!isRemoteContextStale(contextSetterRemoteUrl)) {
      return;
    }
  }

  const freshPayload: unknown = await requestJsonData({
    url: clientFeedUrl,
  });

  if (!freshPayload) {
    announceRemoteContextReady(contextSetterRemoteUrl);
    return;
  }

  const nextContextKeys: ClientContextKeys =
    flattenContextPayload(freshPayload);

  if (Object.keys(nextContextKeys).length === 0) {
    announceRemoteContextReady(contextSetterRemoteUrl);
    return;
  }

  storeContextSetterKeys(nextContextKeys);
  markRemoteContextRevalidated(contextSetterRemoteUrl);
  announceRemoteContextReady(contextSetterRemoteUrl);
}

/**
 * Persists resolved status for this remote URL and notifies listeners.
 *
 * @param contextSetterRemoteUrl - Remote Context Setter feed URL.
 * @returns Nothing.
 */
function announceRemoteContextReady(contextSetterRemoteUrl: string): void {
  markRemoteContextResolved(contextSetterRemoteUrl);
  dispatchContextSetterReadyEvent();
}

/**
 * Builds a client feed URL that bypasses server-side feed caches.
 *
 * @param contextSetterRemoteUrl - Remote Context Setter feed URL.
 * @returns Client feed API URL with `force-fresh=true`.
 */
function getClientFeedUrlWithForceFresh(
  contextSetterRemoteUrl: string
): string {
  try {
    const clientFeedUrl: URL = new URL(
      `/${getClientFeedUrl(contextSetterRemoteUrl)}`,
      window.location.origin
    );

    clientFeedUrl.searchParams.set('force-fresh', 'true');

    return `${clientFeedUrl.pathname}${clientFeedUrl.search}`;
  } catch {
    return `/${getClientFeedUrl(contextSetterRemoteUrl)}`;
  }
}

/**
 * Builds the sessionStorage key used to store a revalidation timestamp.
 *
 * @param contextSetterRemoteUrl - Remote Context Setter feed URL.
 * @returns Namespaced storage key for revalidation tracking.
 */
function getRevalidateStorageKey(contextSetterRemoteUrl: string): string {
  return `${CONTEXT_SETTER_REVALIDATE_STORAGE_PREFIX}:${contextSetterRemoteUrl}`;
}

/**
 * Checks whether a remote Context Setter feed should be re-fetched.
 *
 * @param contextSetterRemoteUrl - Remote Context Setter feed URL.
 * @returns `true` when revalidation is needed; otherwise `false`.
 */
function isRemoteContextStale(contextSetterRemoteUrl: string): boolean {
  try {
    const revalidateTimestampRaw: string | null = window.sessionStorage.getItem(
      getRevalidateStorageKey(contextSetterRemoteUrl)
    );

    if (!revalidateTimestampRaw) return true;

    const revalidateTimestamp: number = Number(
      JSON.parse(revalidateTimestampRaw)
    );

    if (Number.isNaN(revalidateTimestamp)) return true;

    return Date.now() - revalidateTimestamp > CONTEXT_SETTER_REVALIDATE_MS;
  } catch {
    return true;
  }
}

/**
 * Stores the current time as the latest successful revalidation timestamp.
 *
 * @param contextSetterRemoteUrl - Remote Context Setter feed URL.
 * @returns Nothing.
 */
function markRemoteContextRevalidated(contextSetterRemoteUrl: string): void {
  window.sessionStorage.setItem(
    getRevalidateStorageKey(contextSetterRemoteUrl),
    JSON.stringify(Date.now())
  );
}
